// src/services/disciplinePhotoServices.js
import axios from "axios";

// Compat Vite & CRA
const _base =
  (typeof import.meta !== "undefined" &&
    import.meta.env &&
    import.meta.env.VITE_API_URL) ||
  process.env.REACT_APP_API_URL ||
  "";

const baseUrl = _base.replace(/\/$/, "");
const apiUrl = `${baseUrl}/api/disciplines`; // ajuste en '/api/discipline' si besoin

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Upload/Remplace l'image d'une Discipline.
 * - Côté serveur : MASTER + THUMB 400x400 (cover), auto-rotate EXIF.
 * - format par défaut : WEBP (webp|jpg|png).
 * - Retour contrôleur : { id, image_discipline } où image_discipline est un chemin /uploads/...
 *
 * @param {number|string} disciplineId
 * @param {File|Blob} file
 * @param {Object} options
 * @param {"webp"|"jpg"|"png"} [options.format="webp"]
 * @param {function} [options.onUploadProgress]
 * @returns {Promise<{id:number, image_discipline:string}>}
 */
export const uploadDisciplinePhoto = async (disciplineId, file, options = {}) => {
  if (!disciplineId) throw new Error("disciplineId requis");
  if (!file) throw new Error("file requis");

  const { format = "webp", onUploadProgress } = options;

  const form = new FormData();
  // ⚠️ Le contrôleur côté serveur doit faire memoryUpload.single("image")
  form.append("image", file);

  try {
    const { data } = await axios.patch(
      `${apiUrl}/${encodeURIComponent(disciplineId)}/photo?format=${encodeURIComponent(format)}`,
      form,
      {
        headers: {
          ...authHeaders(),
          // Laisse axios régler le Content-Type multipart
        },
        onUploadProgress,
      }
    );
    return data; // { id, image_discipline }
  } catch (error) {
    console.error("[uploadDisciplinePhoto] erreur:", error);
    throw error?.response?.data || error?.message || error;
  }
};

/**
 * Recadre/zoome l'image d'une Discipline existante côté serveur.
 * - Si 'src' est omis, le serveur dérive le MASTER depuis image_discipline actuelle.
 * - x,y,width,height en pixels relativement au MASTER.
 * - Retour: { id, image_discipline } (nouvelle vignette 400x400).
 *
 * @param {number|string} disciplineId
 * @param {Object} crop
 * @param {number} crop.x
 * @param {number} crop.y
 * @param {number} crop.width
 * @param {number} crop.height
 * @param {"webp"|"jpg"|"png"} [crop.format]
 * @param {string} [crop.src] - chemin public /uploads/... si tu veux recadrer une autre image
 * @returns {Promise<{id:number, image_discipline:string}>}
 */
export const cropDisciplinePhoto = async (disciplineId, crop = {}) => {
  if (!disciplineId) throw new Error("disciplineId requis");

  const { x, y, width, height, format, src } = crop;
  const nums = [x, y, width, height];
  if (nums.some((v) => typeof v !== "number" || Number.isNaN(v))) {
    throw new Error("x, y, width, height numériques requis");
    }

  const payload = { x, y, width, height };
  if (format) payload.format = format;
  if (src) payload.src = src;

  try {
    const { data } = await axios.patch(
      `${apiUrl}/${encodeURIComponent(disciplineId)}/photo/crop`,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
      }
    );
    return data; // { id, image_discipline }
  } catch (error) {
    console.error("[cropDisciplinePhoto] erreur:", error);
    throw error?.response?.data || error?.message || error;
  }
};

/**
 * Helper: renvoie une URL exploitable dans <img src=...>
 * - Si commence par /uploads/ => préfixe avec baseUrl (API)
 * - Sinon renvoie tel quel (ex: Cloudinary https://...)
 *
 * @param {string} photoUrl
 * @returns {string}
 */
export const toAbsolutePhotoUrl = (photoUrl) => {
  if (!photoUrl) return "";
  if (photoUrl.startsWith("/uploads/")) {
    return `${baseUrl}${photoUrl}`;
  }
  return photoUrl;
};
